import { useMutation, useQuery } from "@apollo/client";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FileDownload from "@mui/icons-material/FileDownload";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { ResolutionStatusChip } from "components/ResolutionStatusChip";
import { StatusChip } from "components/StatusChip";
import {
  DOWNWARD_MIN_SUSTAINED_MONTHS,
  EXCEEDANCE_DIRECTION_LABELS,
  EXCEEDANCE_RISK_LABELS,
  RISK_ELEVATED_MIN_RUN_MONTHS,
  RISK_HIGH_MIN_RUN_MONTHS,
} from "encamp-shared/src/constants/generatorDiscrepancyDefaults";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  stateGeneratorStatusRank,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import { gql } from "generated-graphql";
import {
  GeneratorStatusExceedanceDirection,
  ResolutionStatus,
  StaffGeneratorStatusChangesQuery,
  StaffGeneratorStatusChangesQueryVariables,
} from "generated-graphql/graphql";
import {
  GridSelectionProvider,
  OmnisearchDataGridV2,
  useGridSelection,
  useOmnisearchState,
} from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useExportData } from "hooks/useExportData";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_GENERATOR_STATUS_CHANGES } from "hooks/useStaffGeneratorStatusChanges";
import { DateTime } from "luxon";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";

import { brandColors } from "../../../util";
import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { DRAWER_PARAM, parseDrawerParam, toDrawerParam } from "./drawerDeepLink";
import { ExceedanceActionDrawer, ExceedanceForDrawer } from "./ExceedanceActionDrawer";
import { formatPeriodSpan } from "./periodFormat";
import { StageFilterSelect } from "./StageFilterSelect";
import { stageLabel } from "./stages";
import { stateStatusPillLabel, StatusTransition } from "./StatusTransition";

// The grid row id is composite. Each row carries an explicit `id` (getRowId) so the V2
// grid selection resolves the model back to row objects for the bulk actions.
type StatusChangeRow =
  StaffGeneratorStatusChangesQuery["staffGeneratorStatusChanges"]["items"][number] & {
    id: string;
  };

/** Exceedance identity + current jump, sent to the lifecycle mutations. */
type ExceedanceKeyInput = {
  facilityId: string;
  source: string;
  periodStart: string;
  reportedFederalStatus: StatusChangeRow["reportedFederalStatus"];
  calculatedFederalStatus: StatusChangeRow["calculatedFederalStatus"];
};

function getRowId(row: Pick<StatusChangeRow, "facilityId" | "source" | "periodStart">): string {
  return `${row.facilityId}-${row.source}-${row.periodStart}`;
}

function toKey(row: StatusChangeRow): ExceedanceKeyInput {
  return {
    facilityId: row.facilityId,
    source: row.source,
    periodStart: row.periodStart,
    reportedFederalStatus: row.reportedFederalStatus,
    calculatedFederalStatus: row.calculatedFederalStatus,
  };
}

function isValidated(row: Pick<StatusChangeRow, "resolutionStatus" | "validatedAt">): boolean {
  return row.resolutionStatus === ResolutionStatus.Unresolved && Boolean(row.validatedAt);
}

/**
 * A distinct chip color per lifecycle stage so each reads uniquely:
 * Needs Validation = error (red), Validated · Ready for Pickup = warning (orange),
 * In Flight / submission started = info (blue), Resolved = success (green), Ignored =
 * neutral grey. Only the two UNRESOLVED sub-states need an override; PENDING /
 * RESOLVED / IGNORED fall through to the chip's status default (info / success / grey).
 */
function stageRole(
  row: Pick<StatusChangeRow, "resolutionStatus" | "validatedAt">
): "warning" | "info" | "success" | "error" | undefined {
  if (row.resolutionStatus === ResolutionStatus.Unresolved) {
    return row.validatedAt ? "warning" : "error";
  }
  return undefined;
}

function toDrawerExceedance(row: StatusChangeRow): ExceedanceForDrawer {
  return {
    facilityId: row.facilityId,
    tenantId: row.tenantId,
    facilityName: row.facilityName,
    organization: row.tenantName,
    state: row.state ?? null,
    source: row.source,
    periodStart: row.periodStart,
    lastPeriodStart: row.lastPeriodStart,
    reportedFederalStatus: row.reportedFederalStatus,
    calculatedFederalStatus: row.calculatedFederalStatus,
    reportedStateStatus: row.reportedStateStatus ?? null,
    calculatedStateStatus: row.calculatedStateStatus ?? null,
    stateDirection: row.stateDirection ?? null,
    changeExplanation: row.changeExplanation,
    resolutionStatus: row.resolutionStatus,
    direction: row.direction ?? null,
    sustainedMonths: row.sustainedMonths ?? null,
    mismatchMonths: row.mismatchMonths ?? null,
    riskLevel: row.riskLevel ?? null,
    detectedAt: row.detectedAt ?? null,
    validatedAt: row.validatedAt ?? null,
    staffAssigneeName: row.staffAssigneeName ?? null,
    openSubmission: row.openSubmission,
    linkedSubmission: row.linkedSubmission,
  };
}

/**
 * Round a day-count metric for display. Renders "—" (not a misleading 0) when the
 * metric is null — e.g. nothing open yet, or no resolved rows to average.
 * (KeyMetric shows `value ?? 0`, so a bare undefined would read as 0.)
 */
function roundDays(value: number | null | undefined): number | string {
  return value != null ? Math.round(value) : "—";
}

const SECTION_LABEL_SX = {
  mb: 1.5,
  display: "block",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "0.7rem",
} as const;

/** One titled cluster of KeyMetric cards in the stats dashboard. */
function StatSection({
  label,
  color,
  children,
}: {
  label: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color={color} sx={SECTION_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
        {children}
      </Box>
    </Box>
  );
}

/** Vertical divider between stat sections; collapses out when the row stacks. */
function SectionDivider() {
  return <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "flex" } }} />;
}

// Default reporting-period window: the last N months (start of month), end open.
const DEFAULT_PERIOD_MONTHS = 3;

function defaultStartDate(): DateTime {
  return DateTime.now().minus({ months: DEFAULT_PERIOD_MONTHS }).startOf("month");
}

function formatDateForApi(dt: DateTime | null): string | undefined {
  // A partially-typed picker value is an invalid DateTime; sending its
  // "Invalid DateTime" string would make the query error while the user types.
  if (!dt || !dt.isValid) return undefined;
  return dt.toFormat("yyyy-MM-dd");
}

/**
 * Collapse a selection to the distinct facilities eligible for a notification: validated
 * UPWARD exceedances only. A downward trend is never filed as a Generator Status Change
 * Notification (the server rejects it), so it's excluded from the create-dialog targets.
 */
function toNotificationFacilities(rows: StatusChangeRow[]): GeneratorStatusChangeFacility[] {
  const byFacilityId = new Map<string, GeneratorStatusChangeFacility>();
  for (const row of rows) {
    if (!isValidated(row) || row.direction !== GeneratorStatusExceedanceDirection.Upward) continue;
    if (!byFacilityId.has(row.facilityId)) {
      byFacilityId.set(row.facilityId, {
        facilityId: row.facilityId,
        tenantId: row.tenantId,
        facilityName: row.facilityName,
        organization: row.tenantName,
        state: row.state ?? null,
        periodStart: row.periodStart,
      });
    }
  }
  return [...byFacilityId.values()];
}

// Stable, module-level grid config (V2 requires stable references for its perf model).
function getItems(data: StaffGeneratorStatusChangesQuery): StatusChangeRow[] {
  return data.staffGeneratorStatusChanges.items.map((item) => ({ ...item, id: getRowId(item) }));
}

function getCount(data: StaffGeneratorStatusChangesQuery): number {
  return data.staffGeneratorStatusChanges.count;
}

// Open (UNRESOLVED) and in-flight (PENDING) rows are actionable in bulk; resolved/ignored
// rows already have a disposition.
function isRowSelectable(params: GridRowParams<StatusChangeRow>): boolean {
  return (
    params.row.resolutionStatus === ResolutionStatus.Unresolved ||
    params.row.resolutionStatus === ResolutionStatus.Pending
  );
}

// Downward trends only surface once sustained this many months; the Downward Trends
// stat card links to `type:DOWNWARD sustained:>=N` so it self-describes and stays
// consistent with the gated count — remove the sustained chip to see the fuller
// (all-durations) list. The constants come from encamp-shared — the same values the
// read path enforces in SQL — so the on-page copy can never drift from the behavior.

const RISK_LABELS: Record<string, string> = EXCEEDANCE_RISK_LABELS;

// Exceedances (UPWARD) sort to the top by default; downward trends below.
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "type", sort: "asc" }];
const INITIAL_COLUMN_VISIBILITY = { stateStatus: false };
// periodStart is excluded because the reporting period is filtered by the top-of-page
// Period Range picker (window variables), not the Filters popover — one period filter,
// and one that survives the stat cards' filter-resetting links. stage is excluded for
// the same reason: the Status filter is the top-of-page multi-select (StageFilterSelect),
// so the popover offering it too would be a second source of truth.
const EXCLUDE_FILTER_COLUMNS = [
  "periodStart",
  "changeExplanation",
  "exceedance",
  "stateDiscrepancy",
  "stateStatus",
  "openSubmission",
  "stage",
];

// Columns capture no component scope (render fns read only `row` + module helpers), so
// they live at module level for a zero-cost stable reference.
const COLUMNS: OmnisearchGridColDef<StatusChangeRow>[] = [
  {
    field: "tenant",
    headerName: "Organization",
    flex: 0.6,
    filterKeyType: "multipleTenant",
    valueGetter: (_, row) => row.tenantName,
  },
  {
    field: "facilityName",
    headerName: "Facility",
    flex: 0.6,
    // Plain text on purpose: the row click opens the drawer, which carries the
    // facility deep link. An in-cell link here competes with the row action.
    valueGetter: (_, row) => row.facilityName,
  },
  // Type: Exceedance (UPWARD) vs Downward trend (DOWNWARD). Sortable so the
  // default "exceedances on top" order holds; filterable so triage can
  // narrow to one type without hiding the other.
  {
    field: "type",
    headerName: "Type",
    flex: 0.4,
    filterKeyType: "enum",
    enumValues: [
      GeneratorStatusExceedanceDirection.Upward,
      GeneratorStatusExceedanceDirection.Downward,
    ],
    renderCell({ row }) {
      if (!row.direction) return "—";
      const isUp = row.direction === GeneratorStatusExceedanceDirection.Upward;
      return (
        <Typography variant="body2" color={isUp ? "warning.main" : "text.secondary"} noWrap>
          {EXCEEDANCE_DIRECTION_LABELS[isUp ? "UPWARD" : "DOWNWARD"]}
        </Typography>
      );
    },
    valueGetter(_, row) {
      return EXCEEDANCE_DIRECTION_LABELS[
        row.direction === GeneratorStatusExceedanceDirection.Upward ? "UPWARD" : "DOWNWARD"
      ];
    },
  },
  // Risk: the derived compliance-risk signal for exceedances. Sustained
  // or recurring UPWARD runs escalate — Elevated once the run spans 3+ months, High
  // at 6+ — so the facilities at real regulatory exposure stand out for follow-up.
  {
    field: "risk",
    headerName: "Risk",
    flex: 0.3,
    filterKeyType: "enum",
    enumValues: ["ELEVATED", "HIGH"],
    enumPresentationFunction: (v) => RISK_LABELS[v] ?? prettyPrintEnumValue(v),
    description:
      `Compliance-risk signal for exceedances left unaddressed: Elevated once the ` +
      `status run spans ${RISK_ELEVATED_MIN_RUN_MONTHS}+ months, High at ` +
      `${RISK_HIGH_MIN_RUN_MONTHS}+.`,
    valueGetter: (_, row) => row.riskLevel,
    renderCell({ row }) {
      if (!row.riskLevel) return "—";
      return (
        <StatusChip
          color={row.riskLevel === "HIGH" ? "error" : "warning"}
          label={RISK_LABELS[row.riskLevel] ?? row.riskLevel}
        />
      );
    },
  },
  // Sustained: how many consecutive calendar months the calculated status has
  // mismatched the reported one, in either direction. The read path gates
  // DOWNWARD rows on it (the Downward Trends card links to `sustained:>=12`),
  // and for UPWARD rows it feeds the compliance-risk signal. The field is
  // the `sustained` filter alias (not `sustainedMonths`) so the column filter,
  // the card's chip, and hand-typed queries all share one key.
  {
    field: "sustained",
    headerName: "Sustained",
    flex: 0.3,
    filterKeyType: "number",
    description:
      "Consecutive calendar months the calculated status has mismatched the " +
      "reported one (either direction). A gap month breaks the run.",
    valueGetter: (_, row) => row.sustainedMonths,
    renderCell({ row }) {
      return row.sustainedMonths ? `${row.sustainedMonths} mo` : "—";
    },
  },
  {
    field: "state",
    headerName: "State/Territory",
    flex: 0.3,
  },
  // Period: the months this exceedance's status run spans. A consolidated row
  // covers every month at the same mismatched calculated status —
  // anchored at the first, extending to the latest — so a six-month repeat reads
  // "Feb 2026 – Jul 2026" on one row instead of six rows. Sorts by the anchor.
  {
    field: "periodStart",
    headerName: "Period",
    flex: 0.4,
    description:
      "Months this discrepancy has covered: first detected month through the " +
      "latest month at the same calculated status (one row per status run).",
    valueGetter(_, row) {
      return formatPeriodSpan(row);
    },
  },
  {
    field: "exceedance",
    headerName: "Discrepancy",
    flex: 1,
    sortable: false,
    description:
      "Reported RCRAInfo status → calculated status. The calculated status is " +
      "reconciled: the highest generator status across this month's e-Manifest, " +
      "vendor & reviewed-document data (no weight lost). Open a row for the by-source breakdown.",
    renderCell({ row }) {
      return (
        <StatusTransition
          oldStatus={row.reportedFederalStatus}
          newStatus={row.calculatedFederalStatus}
        />
      );
    },
    valueGetter(_, row) {
      const reportedLabel = row.reportedFederalStatus
        ? prettyPrintEnumValue(row.reportedFederalStatus)
        : "—";
      const calcLabel = row.calculatedFederalStatus
        ? prettyPrintEnumValue(row.calculatedFederalStatus)
        : "—";
      return `${reportedLabel} → ${calcLabel}`;
    },
  },
  // The state axis of the same comparison, for the ten states with their own tiers.
  {
    field: "stateDiscrepancy",
    headerName: "State Discrepancy",
    flex: 0.7,
    sortable: false,
    description:
      "Reported state status → calculated state status, for facilities in states " +
      "with their own generator tiers. When the state reports no concrete tier, the " +
      "reported side falls back to the reported federal status. Blank when the state " +
      "axis agrees or doesn't apply.",
    renderCell({ row }) {
      if (!row.stateDirection) return "—";
      return (
        <StatusTransition
          oldStatus={row.reportedStateStatus}
          newStatus={row.calculatedStateStatus}
          rank={stateGeneratorStatusRank}
          label={stateStatusPillLabel}
        />
      );
    },
    valueGetter(_, row) {
      if (!row.stateDirection) return "";
      const reportedLabel = stateStatusPillLabel(row.reportedStateStatus ?? null);
      const calcLabel = stateStatusPillLabel(row.calculatedStateStatus ?? null);
      return `${reportedLabel} → ${calcLabel}`;
    },
  },
  {
    field: "changeExplanation",
    headerName: "Explanation",
    flex: 1,
    sortable: false,
    renderCell({ row }) {
      return (
        <Tooltip title={row.changeExplanation} placement="top-start">
          <Typography variant="body2" noWrap sx={{ lineHeight: "inherit" }}>
            {row.changeExplanation}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "detectedAt",
    headerName: "First Detected",
    flex: 0.4,
    // Day-granularity range (unlike Period, which is month-granularity) — detection
    // is a point-in-time event, so staff filter it to specific ingest days.
    filterKeyType: "date",
    description:
      "When the exceedance was first detected — anchors the aging and time-to-" +
      "resolution metrics. A “~” marks an approximate date: the row was backfilled " +
      "when exceedance tracking launched rather than observed live.",
    renderCell({ row }) {
      if (!row.detectedAt) return "—";
      const label = new Date(row.detectedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      if (!row.detectedAtApproximate) {
        return (
          <Typography variant="body2" noWrap>
            {label}
          </Typography>
        );
      }
      // The "~" is cryptic on its own — spell out that it's an approximate,
      // backfilled date on hover, with a dotted underline hinting it's explainable.
      return (
        <Tooltip
          title="Approximate — backfilled when exceedance tracking launched, not observed live. Exceedances detected live show an exact date."
          placement="top-start"
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              borderBottom: "1px dotted",
              borderColor: "text.disabled",
              cursor: "help",
            }}
          >
            {label} ~
          </Typography>
        </Tooltip>
      );
    },
  },
  // Last Updated: when the row last actually changed (the sweep skips no-op
  // writes, so this isn't "last swept"). For RESOLVED rows this is effectively
  // the resolution date — sort it descending on the Resolved view to audit
  // recently-resolved work.
  {
    field: "updatedAt",
    headerName: "Last Updated",
    flex: 0.4,
    filterKeyType: "date",
    description:
      "When the row last changed — resolution/dismissal time for resolved rows; " +
      "the latest validation, link, or newly folded month for open ones.",
    valueGetter(_, row) {
      return new Date(row.updatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  // Month-over-month movement in the CALCULATED state status — not the
  // reported-vs-calculated comparison, which is the State Discrepancy column.
  {
    field: "stateStatus",
    headerName: "Calculated State (MoM)",
    flex: 0.8,
    sortable: false,
    description:
      "Month-over-month movement in the calculated state status (previous run month → " +
      "latest run month). For the reported-vs-calculated comparison, see State Discrepancy.",
    renderCell({ row }) {
      return (
        <StatusTransition
          oldStatus={row.oldStateStatus}
          newStatus={row.newStateStatus}
          rank={stateGeneratorStatusRank}
          label={stateWasteGeneratorStatusToLabel}
        />
      );
    },
    valueGetter(_, row) {
      const oldLabel = stateWasteGeneratorStatusToLabel(row.oldStateStatus ?? null);
      const newLabel = stateWasteGeneratorStatusToLabel(row.newStateStatus ?? null);
      return row.oldStateStatus !== row.newStateStatus ? `${oldLabel} → ${newLabel}` : newLabel;
    },
  },
  // Lifecycle stage chip. Filtered by the top-of-page Status multi-select (which writes
  // the pipe-encoded `stage:` key). Resolved is hidden by default via the server-side
  // default, so the filter's default selection already excludes it.
  {
    field: "stage",
    headerName: "Status",
    flex: 0.55,
    description:
      "Lifecycle stage: Needs Validation → Validated · Ready for Pickup → In Flight " +
      "(a notification is being worked) → Resolved. Dismissed rows show as Ignored. " +
      "Filter by stage with the Status dropdown above the grid; Resolved is hidden by " +
      "default — add it there to include resolved rows. Sorts A→Z by the label shown.",
    renderCell({ row }) {
      return (
        <ResolutionStatusChip
          status={row.resolutionStatus}
          label={stageLabel(row)}
          role={stageRole(row)}
        />
      );
    },
  },
  // Owner of the work — the linked/open submission's assignee (assignment lives
  // on the submission), surfaced by the read path as staffAssigneeName. Sortable by
  // name and filterable by staff user (the read path exposes the assignee's email,
  // which is what the staff-user picker serializes to).
  {
    field: "assignee",
    headerName: "Assignee",
    flex: 0.45,
    filterKeyType: "multipleStaffAnyUser",
    description:
      "Owner of the work, read from the linked (or facility's open) Generator Status " +
      "Change Notification submission — assignment lives on the submission, not the " +
      "exceedance. Filter by staff user; sort by name.",
    renderCell({ row }) {
      return (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.staffAssigneeName ?? "—"}
        </Typography>
      );
    },
  },
  // Peer reviewer of the displayed submission (the staff SubmissionReviewer). Same
  // linked-or-open source as Assignee; sortable by name, filterable by staff user.
  {
    field: "peerReviewer",
    headerName: "Peer Reviewer",
    flex: 0.45,
    filterKeyType: "multipleStaffAnyUser",
    description:
      "Staff peer reviewer on the linked (or facility's open) Generator Status Change " +
      "Notification submission. Filter by staff user; sort by name.",
    renderCell({ row }) {
      return (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.peerReviewerName ?? "—"}
        </Typography>
      );
    },
  },
  {
    field: "openSubmission",
    headerName: "Submission",
    flex: 0.5,
    sortable: false,
    description:
      "The linked Generator Status Change Notification submission (or the facility's " +
      "open one) — its live status and due date. Links to the submission's edit page.",
    renderCell({ row }) {
      const sub = row.linkedSubmission ?? row.openSubmission;
      if (!sub) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }
      const statusLabel = getStatusLabel(sub.status);
      const due = sub.dueDate
        ? ` · due ${new Date(sub.dueDate).toLocaleDateString("en-US", { timeZone: "UTC" })}`
        : "";
      return (
        <Tooltip title={`${sub.title} (${statusLabel}${due})`} placement="top-start">
          <Link
            to={`/o/${row.tenantId}/waste/submissions/${sub.id}/edit`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNew fontSize="small" />
            {statusLabel}
          </Link>
        </Tooltip>
      );
    },
  },
];

const VALIDATE_MUTATION = gql(`
  mutation BulkValidateGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
  ) {
    validateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const UNVALIDATE_MUTATION = gql(`
  mutation BulkUnvalidateGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
  ) {
    unvalidateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const BULK_DISMISS_MUTATION = gql(`
  mutation BulkDismissGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $reason: String!
  ) {
    dismissGeneratorStatusExceedances(exceedances: $exceedances, reason: $reason) {
      id
      status
    }
  }
`);

// Refetch the grid + the stats companion query. Both share the StaffGeneratorStatusChanges
// operation name, so this one entry covers both.
const REFETCH = ["StaffGeneratorStatusChanges"];

/**
 * Bulk-actions dropdown button + menu (presentational). Validate (TPM) and Ignore are
 * per-row; Create Notifications collapses to one filing per facility and is gated to
 * Validated rows only (the validate-before-create flow).
 */
function BulkActionsMenu({
  canValidate,
  canUnvalidate,
  canIgnore,
  canCreate,
  hasSelection,
  onValidate,
  onUnvalidate,
  onIgnore,
  onCreateNotifications,
}: {
  canValidate: boolean;
  canUnvalidate: boolean;
  canIgnore: boolean;
  canCreate: boolean;
  hasSelection: boolean;
  onValidate: () => void;
  onUnvalidate: () => void;
  onIgnore: () => void;
  onCreateNotifications: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  return (
    <>
      <Button
        size="medium"
        variant="contained"
        disabled={!hasSelection}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ExpandMore />}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          whiteSpace: "nowrap",
          backgroundColor: brandColors["encamp-orange"][500],
          color: "white",
          "&:hover": { backgroundColor: brandColors["encamp-orange"][700] },
        }}
      >
        Bulk Actions
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        <MenuItem
          disabled={!canValidate}
          onClick={() => {
            close();
            onValidate();
          }}
        >
          Validate (mark ready for pickup)
        </MenuItem>
        <MenuItem
          disabled={!canUnvalidate}
          onClick={() => {
            close();
            onUnvalidate();
          }}
        >
          Un-validate (back to needs validation)
        </MenuItem>
        <Tooltip
          title={canCreate ? "" : "Validate the selected rows first"}
          placement="left"
          disableInteractive
        >
          <span>
            <MenuItem
              disabled={!canCreate}
              onClick={() => {
                close();
                onCreateNotifications();
              }}
            >
              Create Generator Status Change Notifications
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem
          disabled={!canIgnore}
          onClick={() => {
            close();
            onIgnore();
          }}
        >
          Dismiss as not a discrepancy…
        </MenuItem>
      </Menu>
    </>
  );
}

/** Bulk dismiss dialog — a reason is required (human in the loop). */
function BulkDismissDialog({
  open,
  count,
  onClose,
  onConfirm,
  submitting,
}: {
  open: boolean;
  count: number;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  return (
    <Dialog
      open={open}
      onClose={() => {
        setReason("");
        onClose();
      }}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 520, maxWidth: "90vw" } } }}
      title={`Dismiss ${count} discrepanc${count === 1 ? "y" : "ies"}`}
      description="Dismissals are recorded with their reason on the audit trail and feed the engine-quality (false-positive) signal."
      primaryActionButtonProps={{
        onClick: () => onConfirm(trimmed),
        loading: submitting,
        loadingText: "Dismissing...",
        hideStartIcon: true,
        saveText: "Dismiss",
        disabled: submitting || trimmed.length === 0,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      <TextField
        label="Reason (why are these not discrepancies?)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        autoFocus
      />
    </Dialog>
  );
}

/**
 * Bulk actions wired to the V2 grid selection. Reads the current selection via
 * useGridSelection (the grid auto-registers with the surrounding GridSelectionProvider),
 * gates the three actions, runs the mutations, and owns the Create + Dismiss dialogs.
 * Rendered as a toolbar command button.
 */
function GeneratorStatusBulkActions() {
  const alerts = useAlerts();
  const { selectedCount, getSelectedRows, clearSelection } = useGridSelection<StatusChangeRow>();

  const [validate] = useMutation(VALIDATE_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [unvalidate] = useMutation(UNVALIDATE_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [dismiss, { loading: dismissing }] = useMutation(BULK_DISMISS_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  // Snapshot the selection when a dialog opens so it can't shift mid-flow.
  const [pendingFacilities, setPendingFacilities] = useState<GeneratorStatusChangeFacility[]>([]);
  const [pendingDismiss, setPendingDismiss] = useState<StatusChangeRow[]>([]);

  // getSelectedRows() reads the grid's live selection; useGridSelection re-renders this
  // component whenever the selection changes (via selectedCount), so a plain read per
  // render stays current without memoizing on a value the callback doesn't reference.
  const selectedRows = getSelectedRows();
  const validatableRows = selectedRows.filter(
    (r) => r.resolutionStatus === ResolutionStatus.Unresolved && !r.validatedAt
  );
  // Only currently-validated rows can be un-validated (inverse of validate).
  const unvalidatableRows = selectedRows.filter(isValidated);
  const notificationFacilities = toNotificationFacilities(selectedRows);

  const canValidate = validatableRows.length > 0;
  const canUnvalidate = unvalidatableRows.length > 0;
  const canIgnore = selectedRows.length > 0;
  // Hard gate: every selected row must be Validated, and at least one must be an UPWARD
  // exceedance (downward trends aren't filed) — otherwise the create dialog has nothing
  // valid to target. notificationFacilities is already filtered to validated UPWARD rows.
  const canCreate =
    selectedRows.length > 0 && selectedRows.every(isValidated) && notificationFacilities.length > 0;

  const runValidate = useCallback(async () => {
    if (validatableRows.length === 0) return;
    const count = validatableRows.length;
    try {
      await validate({ variables: { exceedances: validatableRows.map(toKey) } });
      alerts.success(
        `Validated ${count} discrepanc${count === 1 ? "y" : "ies"} — ready for pickup.`
      );
      clearSelection();
    } catch (e) {
      alerts.error("Failed to validate the selected discrepancies.", e);
    }
  }, [validate, validatableRows, clearSelection, alerts]);

  const runUnvalidate = useCallback(async () => {
    if (unvalidatableRows.length === 0) return;
    const count = unvalidatableRows.length;
    try {
      await unvalidate({ variables: { exceedances: unvalidatableRows.map(toKey) } });
      alerts.success(
        `Un-validated ${count} discrepanc${count === 1 ? "y" : "ies"} — back to needs validation.`
      );
      clearSelection();
    } catch (e) {
      alerts.error("Failed to un-validate the selected discrepancies.", e);
    }
  }, [unvalidate, unvalidatableRows, clearSelection, alerts]);

  const openDismiss = useCallback(() => {
    setPendingDismiss(selectedRows);
    setDismissOpen(true);
  }, [selectedRows]);

  const runDismiss = useCallback(
    async (reason: string) => {
      const count = pendingDismiss.length;
      try {
        await dismiss({ variables: { exceedances: pendingDismiss.map(toKey), reason } });
        alerts.success(`Dismissed ${count} discrepanc${count === 1 ? "y" : "ies"}.`);
        setDismissOpen(false);
        clearSelection();
      } catch (e) {
        alerts.error("Failed to dismiss the selected discrepancies.", e);
      }
    },
    [dismiss, pendingDismiss, clearSelection, alerts]
  );

  const openCreate = useCallback(() => {
    setPendingFacilities(notificationFacilities);
    setCreateOpen(true);
  }, [notificationFacilities]);

  return (
    <>
      <BulkActionsMenu
        hasSelection={selectedCount > 0}
        canValidate={canValidate}
        canUnvalidate={canUnvalidate}
        canIgnore={canIgnore}
        canCreate={canCreate}
        onValidate={runValidate}
        onUnvalidate={runUnvalidate}
        onIgnore={openDismiss}
        onCreateNotifications={openCreate}
      />
      <CreateGeneratorStatusChangeSubmissionsDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        facilities={pendingFacilities}
        onSuccess={clearSelection}
      />
      <BulkDismissDialog
        open={dismissOpen}
        count={pendingDismiss.length}
        onClose={() => setDismissOpen(false)}
        onConfirm={runDismiss}
        submitting={dismissing}
      />
    </>
  );
}

/**
 * "Assigned to me" toggle. Reporting specialists auto-own the notification they
 * create, so this is about visibility — narrowing the grid to the rows they own
 * without hunting through Fulfillment. A Switch (matching the Fulfillment activities
 * table) that flips the same `assignee` omnisearch key the Assignee filter uses, set
 * to the current user's email (the staff-user picker serializes to email).
 */
function AssignedToMeToggle() {
  const { user } = useCurrentUser();
  const { getOmnisearchValue, editOmnisearchKeyValues, removeOmnisearchKeyValue } =
    useOmnisearchState();
  const email = user?.email;
  const active = Boolean(email) && getOmnisearchValue("assignee") === email;
  return (
    <FormControlLabel
      sx={{ marginRight: 0, whiteSpace: "nowrap" }}
      control={
        <Switch
          checked={active}
          disabled={!email}
          onChange={(_, checked) =>
            checked && email
              ? editOmnisearchKeyValues([["assignee", email]])
              : removeOmnisearchKeyValue("assignee")
          }
        />
      }
      label="Assigned to me"
    />
  );
}

/**
 * Export the dashboard to CSV for monthly customer status audits. The worker always
 * exports every lifecycle stage (Resolved and Ignored included), whatever the Status
 * filter shows on screen — an audit must be complete. Every other filter applies,
 * including the Period Range, which lives in component state (not omnisearch) and so
 * is passed explicitly here.
 */
function ExportCsvButton({
  startDateStr,
  endDateStr,
}: {
  startDateStr: string | undefined;
  endDateStr: string | undefined;
}) {
  const alerts = useAlerts();
  const { getOmnisearch } = useOmnisearchState();
  // The grid mutates in place (validate / dismiss / link) — a cached signed URL
  // would silently serve stale rows, so caching is off.
  const { startExport, status: exportStatus } = useExportData({
    autoDownload: true,
    timeoutInSeconds: 300,
    disableCache: true,
  });

  const handleExport = useCallback(() => {
    alerts.info("Export started; download will open when ready.");
    startExport({
      exportType: "generatorStatusChangesForStaff",
      fileName: "generator-status-changes-staff",
      filter: {
        search: getOmnisearch(),
        sort: [
          { field: "tenantName", sort: "asc" },
          { field: "facilityName", sort: "asc" },
        ],
        startDate: startDateStr,
        endDate: endDateStr,
      },
    });
  }, [alerts, startExport, getOmnisearch, startDateStr, endDateStr]);

  return (
    <Tooltip title="Export the current filters to CSV. Every lifecycle stage is included (Resolved and Ignored too), regardless of the Status filter. Other defaults still apply: short downward trends stay excluded unless a Type or Sustained filter is active.">
      {/* span keeps the tooltip working while the button is disabled */}
      <span>
        <Button
          size="small"
          variant="text"
          startIcon={<FileDownload />}
          disabled={exportStatus === "loading"}
          onClick={handleExport}
          sx={{ whiteSpace: "nowrap" }}
        >
          Export CSV
        </Button>
      </span>
    </Tooltip>
  );
}

export const GeneratorStatusChanges = () => {
  useBreadcrumb({ label: "Generator Status Changes - Potential Discrepancies" });

  const [startDate, setStartDate] = useState<DateTime | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const startDateStr = formatDateForApi(startDate);
  const endDateStr = formatDateForApi(endDate);

  const { omnisearch, createUrlFromOmnisearchKeyValues } = useOmnisearchState();

  // The V2 grid owns its own query and doesn't surface the response, so the dashboard cards
  // read stats from a lightweight companion query (same operation, pageSize 1) scoped to the
  // same search + period window. It shares the StaffGeneratorStatusChanges operation name, so
  // the mutations' refetch covers it too.
  const { data: statsData, loading: statsLoading } = useQuery(GET_STAFF_GENERATOR_STATUS_CHANGES, {
    variables: {
      search: omnisearch,
      page: 0,
      pageSize: 1,
      startDate: startDateStr,
      endDate: endDateStr,
      // This companion query is the ONLY one that computes stats — the grid and
      // drawer-hydration queries skip the aggregate pass entirely.
      withStats: true,
    },
    fetchPolicy: "cache-and-network",
  });
  const stats = statsData?.staffGeneratorStatusChanges.stats;

  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): StaffGeneratorStatusChangesQueryVariables => ({
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      startDate: startDateStr,
      endDate: endDateStr,
      // The stat cards read from the companion query above; recomputing the
      // aggregates on every page/sort change would double the query cost.
      withStats: false,
    }),
    [startDateStr, endDateStr]
  );

  const [selectedExceedance, setSelectedExceedance] = useState<ExceedanceForDrawer | null>(null);

  // Drawer deep link: the open drawer's identity mirrors into `?drawer=...` so the
  // view is shareable; loading a URL with the param hydrates and opens the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerKey = useMemo(() => parseDrawerParam(searchParams.get(DRAWER_PARAM)), [searchParams]);
  // A locally-initiated param removal (close button, hydration miss) leaves the
  // stale ?drawer= visible until the router transition lands; that gap must not
  // read as "needs hydration" (it would flash a skeleton drawer and fire a
  // pointless row query mid-close).
  const locallyClosingRef = useRef(false);
  const setDrawerParam = useCallback(
    (value: string | null) => {
      locallyClosingRef.current = value === null;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(DRAWER_PARAM, value);
          } else {
            next.delete(DRAWER_PARAM);
          }
          return next;
        },
        // Opening PUSHES a history entry so the browser back button lands on the
        // param-less URL (and the sync effect below closes the drawer). Closing
        // replaces — going back after an explicit close shouldn't reopen it.
        // preventScrollReset keeps <ScrollRestoration /> from yanking the page to
        // the top: the drawer overlays the grid, so the scroll position must hold.
        { replace: value === null, preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  const onRowClick = useCallback(
    (params: GridRowParams<StatusChangeRow>) => {
      setSelectedExceedance(toDrawerExceedance(params.row));
      setDrawerParam(toDrawerParam(params.row));
    },
    [setDrawerParam]
  );
  const closeDrawer = useCallback(() => {
    setSelectedExceedance(null);
    setDrawerParam(null);
  }, [setDrawerParam]);

  // Browser back/forward: the URL param is the source of truth — but only its
  // REMOVAL (a non-null → null transition) closes the drawer. A row click opens
  // the drawer locally before React Router's startTransition lands the ?drawer=
  // param; treating "param not there yet" as "param removed" closed the drawer in
  // that gap, and it only reopened after the deep-link hydration query returned —
  // which made every row click feel blocked on the network.
  const prevDrawerKeyRef = useRef(drawerKey);
  useEffect(() => {
    const prevDrawerKey = prevDrawerKeyRef.current;
    prevDrawerKeyRef.current = drawerKey;
    if (prevDrawerKey !== null && drawerKey === null && selectedExceedance !== null) {
      setSelectedExceedance(null);
    }
  }, [drawerKey, selectedExceedance]);

  // Deep-link hydration. The linked row may sit outside the grid's current page —
  // or outside its default filters entirely (resolved rows and short downward runs
  // are hidden by default) — so it's fetched by a targeted query that pins the
  // facility and opts into every status and direction. Identity-compared (not just
  // open/closed) so back/forward between two drawer entries — or a "separate
  // discrepancy" cross-link inside the drawer — swaps the drawer to the new row.
  const selectedMatchesKey =
    selectedExceedance !== null &&
    drawerKey !== null &&
    selectedExceedance.facilityId === drawerKey.facilityId &&
    selectedExceedance.source === drawerKey.source &&
    selectedExceedance.periodStart === drawerKey.periodStart;
  // The close has settled once the param is gone; clear the flag so a browser
  // Forward (which re-adds the param without calling setDrawerParam) rehydrates.
  useEffect(() => {
    if (drawerKey === null) {
      locallyClosingRef.current = false;
    }
  }, [drawerKey]);
  const needsDrawerHydration =
    drawerKey !== null && !selectedMatchesKey && !locallyClosingRef.current;
  const { data: drawerRowData } = useQuery(GET_STAFF_GENERATOR_STATUS_CHANGES, {
    variables: {
      search: drawerKey
        ? `facilityId:${drawerKey.facilityId} status:UNRESOLVED|PENDING|RESOLVED|IGNORED type:UPWARD|DOWNWARD`
        : "",
      page: 0,
      pageSize: 100,
      withStats: false,
    },
    skip: !needsDrawerHydration,
    fetchPolicy: "cache-and-network",
  });
  useEffect(() => {
    if (!needsDrawerHydration || !drawerKey || !drawerRowData) return;
    const row = drawerRowData.staffGeneratorStatusChanges.items.find(
      (item) =>
        // facilityId re-checked so a stale cached result for a previous key can't
        // hydrate the wrong facility's row into the drawer.
        item.facilityId === drawerKey.facilityId &&
        item.source === drawerKey.source &&
        item.periodStart === drawerKey.periodStart
    );
    if (row) {
      setSelectedExceedance(toDrawerExceedance({ ...row, id: getRowId(row) }));
    } else {
      // The linked exceedance no longer exists (e.g. consolidated away) — drop the
      // param rather than refetching forever.
      setDrawerParam(null);
    }
  }, [needsDrawerHydration, drawerKey, drawerRowData, setDrawerParam]);

  const commandButtons = useMemo(
    () => [
      <AssignedToMeToggle key="gsc-assigned-to-me" />,
      <GeneratorStatusBulkActions key="gsc-bulk-actions" />,
    ],
    []
  );

  const handleStartDateChange = useCallback((value: DateTime | null) => setStartDate(value), []);
  const handleEndDateChange = useCallback((value: DateTime | null) => setEndDate(value), []);

  return (
    <GridSelectionProvider>
      <Stack py={3} spacing={2}>
        {/* Period range filter — top-of-page window state, deliberately outside the
            Filters dialog so it survives the stat cards' filter-resetting links.
            First Detected filters by day in the Filters dialog. */}
        <Paper elevation={0} sx={{ px: 3, py: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              gap={1.5}
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <StageFilterSelect />
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    label="Period Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    views={["year", "month"]}
                    slotProps={{
                      textField: { variant: "standard", size: "small", sx: { width: 150 } },
                    }}
                  />
                  <Typography variant="body1" color="text.secondary" sx={{ pb: 0.5 }}>
                    –
                  </Typography>
                  <DatePicker
                    label="Period End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    views={["year", "month"]}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        size: "small",
                        sx: { width: 150 },
                        placeholder: "Current",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>
              <ExportCsvButton startDateStr={startDateStr} endDateStr={endDateStr} />
            </Stack>
            {/* Spell out the invisible defaults so the counts don't read as wrong: the
                period window above, plus two read-path rules the grid applies unless the
                caller opts out via filters. */}
            <Typography variant="body2" color="text.secondary">
              Defaults: reporting periods from the last {DEFAULT_PERIOD_MONTHS} months (adjust
              above). Repeat months at the same calculated status fold into one row spanning its
              Period range. Downward trends appear only once sustained{" "}
              {DOWNWARD_MIN_SUSTAINED_MONTHS}+ consecutive months — filter by Type or Sustained to
              include shorter runs. Resolved discrepancies are hidden — use the Status filter above
              to include them. Risk marks exceedances left unaddressed{" "}
              {RISK_ELEVATED_MIN_RUN_MONTHS}+ months (Elevated) or {RISK_HIGH_MIN_RUN_MONTHS}+
              (High). Export CSV always includes every status, Resolved included (the downward rule
              above still applies).
            </Typography>
          </Stack>
        </Paper>

        {/* Stats — one dashboard card grouped into Findings · Queue · Time to
            Resolution · Aging (mirrors the Fulfillment WorkflowOverviewKeyMetrics
            layout). px:3 keeps left/right edges aligned with the filter and grid.
            Each card links with keysToKeep: [] — clicking it resets to just that bucket
            (not additive), intentionally, so the filtered view matches the card's count. */}
        <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={3} sx={{ minWidth: 0 }}>
            <StatSection label="Findings" color="warning.main">
              <KeyMetric
                name="Exceedances"
                value={stats?.exceedanceCount}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["type", GeneratorStatusExceedanceDirection.Upward]],
                  keysToKeep: [],
                })}
              />
              <KeyMetric
                name="Downward Trends"
                value={stats?.downwardTrendCount}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [
                    ["type", GeneratorStatusExceedanceDirection.Downward],
                    ["sustained", `>=${DOWNWARD_MIN_SUSTAINED_MONTHS}`],
                  ],
                  keysToKeep: [],
                })}
              />
              <KeyMetric
                name="Calculated as LQG"
                value={stats?.exceedancesToLqg}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["calculatedFederalStatus", "LARGE_QUANTITY_GENERATOR"]],
                  keysToKeep: [],
                })}
              />
              <KeyMetric
                name="Calculated as SQG"
                value={stats?.exceedancesToSqg}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["calculatedFederalStatus", "SMALL_QUANTITY_GENERATOR"]],
                  keysToKeep: [],
                })}
              />
            </StatSection>

            <SectionDivider />

            <StatSection label="Queue" color="info.main">
              <KeyMetric
                name="Awaiting Validation"
                value={stats?.awaitingValidation}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["stage", "AWAITING_VALIDATION"]],
                  keysToKeep: [],
                })}
              />
              <KeyMetric
                name="Ready for Pickup"
                value={stats?.readyForPickup}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["stage", "READY_FOR_PICKUP"]],
                  keysToKeep: [],
                })}
              />
              <KeyMetric
                name="In Flight"
                value={stats?.inFlight}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["stage", "IN_FLIGHT"]],
                  keysToKeep: [],
                })}
              />
              {/* Its own card rather than folded into In Flight: both are PENDING, but
                  they filter to different stages, so one card would show a total its
                  click-through grid doesn't. */}
              <KeyMetric
                name="Awaiting Correction"
                value={stats?.awaitingCorrection}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["stage", "AWAITING_CORRECTION"]],
                  keysToKeep: [],
                })}
              />
              {/* Resolved work is hidden from the default grid; this card keeps it
                  discoverable/auditable (esp. for monthly status audits) — clicking it
                  filters to stage:RESOLVED, which lifts the default hide-resolved rule. */}
              <KeyMetric
                name="Resolved"
                value={stats?.resolvedCount}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["stage", "RESOLVED"]],
                  keysToKeep: [],
                })}
              />
            </StatSection>

            <SectionDivider />

            <StatSection label="Time to Resolution" color="text.secondary">
              <KeyMetric
                name="Median Days to Validation"
                value={roundDays(stats?.medianDaysToValidation)}
                loading={statsLoading}
              />
              <KeyMetric
                name="P90 Days to Validation"
                value={roundDays(stats?.p90DaysToValidation)}
                loading={statsLoading}
              />
              <KeyMetric
                name="Median Days to Resolution"
                value={roundDays(stats?.medianDaysToResolution)}
                loading={statsLoading}
              />
              <KeyMetric
                name="P90 Days to Resolution"
                value={roundDays(stats?.p90DaysToResolution)}
                loading={statsLoading}
              />
            </StatSection>

            <SectionDivider />

            <StatSection label="Aging" color="error.main">
              <KeyMetric
                name="Oldest Open (days)"
                value={roundDays(stats?.oldestOpenDays)}
                loading={statsLoading}
              />
              <KeyMetric
                name="Open > 30 Days"
                value={stats?.openOver30Days}
                loading={statsLoading}
              />
              {/* Sustained/recurring exceedances at escalated compliance risk —
                  the link filters to the same rows the count covers. */}
              <KeyMetric
                name="Elevated Risk"
                value={stats?.elevatedRiskCount}
                loading={statsLoading}
                href={createUrlFromOmnisearchKeyValues({
                  keyValues: [["risk", "ELEVATED|HIGH"]],
                  keysToKeep: [],
                })}
              />
            </StatSection>
          </Stack>
        </Paper>

        {/* Data Grid (Omnisearch V2) */}
        <OmnisearchDataGridV2<
          StaffGeneratorStatusChangesQuery,
          StaffGeneratorStatusChangesQueryVariables,
          StatusChangeRow
        >
          query={GET_STAFF_GENERATOR_STATUS_CHANGES}
          persistenceKey="staffGeneratorStatusChanges"
          columns={COLUMNS}
          getItems={getItems}
          getCount={getCount}
          getRowId={getRowId}
          buildVariables={buildVariables}
          initialPageSize={25}
          initialSortModel={INITIAL_SORT_MODEL}
          initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY}
          excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={isRowSelectable}
          commandButtons={commandButtons}
          onRowClick={onRowClick}
        />

        <ExceedanceActionDrawer
          exceedance={selectedExceedance}
          // Deep-link hydration in flight: open the shell with skeletons instead
          // of leaving the click apparently dead until the row query returns.
          loading={needsDrawerHydration}
          onClose={closeDrawer}
          onResolved={closeDrawer}
        />
      </Stack>
    </GridSelectionProvider>
  );
};
